import {
  ActionLabel,
  getPlaceholder,
  getPlaceholderForConversionReportType,
  getPlaceholderForCustomReportType,
  getStatus,
  getStatusForConversionReportType,
  getStatusForCustomReportType,
  isFromScratch,
  isMinimalFiling,
  statusKeys
} from '../../../data';

import { ClipLoader } from 'react-spinners';
import OutlinedNavigateIcon from '../../../../../../assets/icons/navigate-outlined.svg?react';
import { useAutomateExtraction } from '../../../../../../services/automation.service';
import toast from 'react-hot-toast';
import { useEffect, useMemo, useState } from 'react';
import { REPORT_TYPES } from '../../../../../../consts/reportTypes';
import { useNavigate } from 'react-router-dom';
import { FaArrowRight } from 'react-icons/fa6';
import { PermissionGuard } from '../../../../../../components/PermissionGuard';
import { Tooltip } from '@mantine/core';
import { extractApiError } from '../../../../../../utils';

export const ExtractionColumn = ({
  period,
  isLoadingPeriods,
  onOpenDrawer,
  refetch,
  workflowId,
  reportType = REPORT_TYPES.ANNEX_IV,
  canManageWorkflow = true,
  onOpenInitialExtractionDrawer
}) => {
  const navigate = useNavigate();
  const status = useMemo(() => {
    if (reportType === REPORT_TYPES.CONVERSION) return getStatusForConversionReportType(period);
    else if (reportType === REPORT_TYPES.CUSTOM) return getStatusForCustomReportType(period);

    return getStatus(period);
  }, [reportType, period]);
  const isScratch = isFromScratch(period);
  const placeholder = useMemo(() => {
    if (reportType === REPORT_TYPES.CONVERSION)
      return getPlaceholderForConversionReportType(period, reportType);
    else if (reportType === REPORT_TYPES.CUSTOM)
      return getPlaceholderForCustomReportType(period, reportType);

    return getPlaceholder(period, reportType);
  }, [reportType, period]);
  const automateExtraction = useAutomateExtraction();

  const [allowToShowLoadingPeriods, setAllowToShowLoadingPeriods] = useState(false);

  useEffect(() => {
    if (!isLoadingPeriods) setAllowToShowLoadingPeriods(false);
  }, [isLoadingPeriods]);

  const onAutoExtract = () => {
    if (isFromScratch(period)) {
      navigate(`/data-extraction/${workflowId}/${period.id}?tab=extraction`);
      return;
    }

    automateExtraction.mutate(
      {
        period_id: period?.id
      },
      {
        onSuccess: () => {
          refetch();
          setAllowToShowLoadingPeriods(true);
          toast.success('Automated Extraction have been successfully applied', {
            position: 'bottom-center'
          });
        },
        onError: err => {
          toast.error(extractApiError(err, 'Unable to start automatic extraction.'));
        }
      }
    );
  };
  const onClick = () => {
    if (period.template_type === 'sratch') {
      onOpenInitialExtractionDrawer(period?.id);
      return;
    }

    onOpenDrawer(period?.id);
  };

  const onOpenIssues = () => {
    onOpenDrawer(period?.id);
  };

  const isNextStatus =
    status === statusKeys.EXTRACTION ||
    status === statusKeys.TRANSFORMATION ||
    status === statusKeys.REPORT ||
    status === statusKeys.APPROVAL ||
    status === statusKeys.SUBMISSION ||
    status === statusKeys.END;

  const hasWarningsOrErrors =
    !!period?.extraction_stats?.failed_extractions_number ||
    !!period?.extraction_stats?.warned_extractions_number ||
    !!period?.extraction_stats?.in_progress_extractions_number ||
    !!period?.extraction_stats?.not_completed_extractions_number;

  const isNotCompleted =
    period?.extraction_stats?.failed_extractions_number === 0 &&
    period?.extraction_stats?.warned_extractions_number === 0 &&
    period?.extraction_stats?.in_progress_extractions_number === 0 &&
    period?.extraction_stats?.successful_extractions_number === 0 &&
    period?.extraction_stats?.not_completed_extractions_number === 0;

  if (automateExtraction.isLoading)
    return <ActionLabel style={{ width: 150 }} placeholder="Extracting..." />;

  if (isLoadingPeriods && allowToShowLoadingPeriods)
    return <ActionLabel style={{ width: 150 }} placeholder="Extracting..." />;

  if (isMinimalFiling(period)) return <ActionLabel style={{ width: 150 }} placeholder="N/A" />;

  if (period.extraction_progress_status === 'In progress' && isNotCompleted) {
    return (
      <ActionLabel
        label={period?.extraction_stats?.not_completed_extractions_number + ' not extracted'}
        color="#7b7b7b"
        onClick={onOpenIssues}
        rightIcon={<FaArrowRight />}
      />
    );
  }

  if (
    status === statusKeys.EXTRACTION &&
    (!hasWarningsOrErrors || period?.extraction_progress_status === 'Not started')
  )
    return (
      <PermissionGuard allowed={canManageWorkflow}>
        <Tooltip
          label="You do not have permission to start extraction"
          disabled={canManageWorkflow}
        >
          <ActionLabel
            style={{ width: 150 }}
            label="Start Extraction"
            rightIcon={<OutlinedNavigateIcon />}
            onClick={onAutoExtract}
            color={!canManageWorkflow ? 'gray' : undefined}
            disabled={!canManageWorkflow}
          />
        </Tooltip>
      </PermissionGuard>
    );

  if (hasWarningsOrErrors)
    return (
      <>
        {period?.extraction_stats?.successful_extractions_number > 0 && (
          <ActionLabel
            label={period?.extraction_stats?.successful_extractions_number + ' successful'}
            color="#40C057"
            onClick={onOpenIssues}
            rightIcon={<FaArrowRight />}
          />
        )}

        {period?.extraction_stats?.failed_extractions_number > 0 && (
          <ActionLabel
            label={period?.extraction_stats?.failed_extractions_number + ' issues'}
            color="#FA5252"
            onClick={onOpenIssues}
            rightIcon={<FaArrowRight />}
          />
        )}

        {period?.extraction_stats?.warned_extractions_number > 0 && (
          <ActionLabel
            label={period?.extraction_stats?.warned_extractions_number + ' issues'}
            color="#FD7E14"
            onClick={onOpenIssues}
            rightIcon={<FaArrowRight />}
          />
        )}

        {period?.extraction_stats?.not_completed_extractions_number > 0 && (
          <ActionLabel
            label={period?.extraction_stats?.not_completed_extractions_number + ' not extracted'}
            color="#7b7b7b"
            onClick={onOpenIssues}
            rightIcon={<FaArrowRight />}
          />
        )}

        {period?.extraction_stats?.in_progress_extractions_number > 0 && (
          <ActionLabel
            label={period?.extraction_stats?.in_progress_extractions_number + ' extracting'}
            color="#228BE6"
            onClick={onOpenIssues}
            rightIcon={<FaArrowRight />}
          />
        )}
      </>
    );

  if (
    period.extraction_progress_status === 'In progress' &&
    !isScratch &&
    !period?.extraction_stats?.successful_extractions_number
  )
    return (
      <ActionLabel
        style={{ width: 150 }}
        label="In progress"
        rightIcon={<ClipLoader size={16} color="#228BE6" />}
        onClick={onClick}
      />
    );

  if (
    (period.extraction_progress_status === 'Completed' ||
      isScratch ||
      period?.extraction_stats?.successful_extractions_number > 0) &&
    isNextStatus
  )
    return (
      <ActionLabel
        style={{ width: 150 }}
        label="Finished"
        color="#40C057"
        onClick={onClick}
        rightIcon={<FaArrowRight />}
      />
    );

  return <ActionLabel placeholder={placeholder} />;
};
